
import { db, collection, doc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, getDoc, query, where, orderBy, limit, serverTimestamp, functions } from "../../firebaseClient";
import type { ComplianceCard, ComplianceCardType, ObligationTemplate, CrawlerRunLog, CoAssistSection } from "../types/compliance";
import { createOrResetApprovalFlow } from "./approvalFlowService";
import { createApprovalRequest } from "./approvalRequestsService";
import { logActivity } from "./activityLog";
import { mapOwnerDeptToCode, resolveAgencyName } from "./complianceSourceResolver";
import { getDeptNameByCode } from "../constants/departments";

// Raw CSV Content (Injected to avoid fetch/fs issues in pure client)
// This matches the file ui/assets/seed/nine_obligations.csv
const SEED_CSV_CONTENT = `義務項目,公告標題,公告日期,公告連結,附檔連結,主旨,摘要(750字+),主責單位
1,工作規則核備,113/05/20 (修正),https://law.moj.gov.tw/LawClass/LawAll.aspx?pcode=N0030001,https://www.mol.gov.tw/media/123/workrules_guide.pdf,勞動基準法第70條工作規則修訂,雇主僱用勞工人數在三十人以上者，應依其事業性質，就工時、休息、休假、工資、津貼及獎金、紀律、考勤、請假、獎懲及升遷、受僱、解僱、資遣、離職及退休、災害傷病補償及撫卹、福利措施等相關事項訂立工作規則，報請主管機關核備後並公開揭示之。違反者處新臺幣二萬元以上三十萬元以下罰鍰。,行政人事部 (AP)
2,勞資會議實施,112/12/01 (函釋),https://laws.mol.gov.tw/FLAW/FLAWDAT01.aspx?lsid=FL014931,https://www.mol.gov.tw/media/456/meeting_minutes_template.doc,勞資會議實施辦法更新,事業單位應舉辦勞資會議。其事業場所勞工人數在三十人以上者，亦應分別舉辦之。勞資會議由勞資雙方同數代表組成，其代表人數視事業單位人數多寡各為二人都十五人。勞資會議至少每三個月舉辦一次，必要時得召開臨時會議。會議紀錄應發放給勞工或公告。,行政人事部 (AP)
3,性騷擾防治措施,113/03/08 (施行),https://law.moj.gov.tw/LawClass/LawAll.aspx?pcode=N0030014,https://www.mol.gov.tw/media/789/sexual_harassment_prevention.pdf,性別平等工作法修法重點,雇主應防治性騷擾行為之發生。其僱用受僱者三十人以上者，應訂定性騷擾防治措施、申訴及懲戒規範，並在工作場所公開揭示。雇主於知悉性騷擾之情形時，應採取立即有效之糾正及補救措施。本次修法強化雇主防治義務，並增訂被害人申訴管道及法律扶助。,行政人事部 (AP)
4,職業安全衛生計畫,112/06/15 (修正),https://law.moj.gov.tw/LawClass/LawAll.aspx?pcode=N0060001,https://www.osha.gov.tw/media/111/safety_plan_guideline.pdf,職業安全衛生法第23條規定,雇主應依其事業單位之規模、性質，訂定職業安全衛生管理計畫；並應依規定置職業安全衛生業務主管及管理人員。勞工人數在三十人以上者，應訂定職業安全衛生工作守則，報經勞動檢查機構備查後，公告實施。違反者處新臺幣三萬元以上十五萬元以下罰鍰。,行政倉儲部 (LI/AW)
5,食品安全監測計畫,113/01/01 (生效),https://law.moj.gov.tw/LawClass/LawAll.aspx?pcode=L0040001,https://www.fda.gov.tw/media/222/food_safety_plan.pdf,食品安全衛生管理法第7條,食品業者應實施自主管理，訂定食品安全監測計畫，確保食品衛生安全。經中央主管機關公告類別及規模之食品業者，應檢驗精確度及檢驗量能，自行或委託相關機關（構）、法人或團體辦理檢驗。應保存產品原材料、半成品及成品之來源及流向證明文件至少五年。,品保部 (QC)
6,食品良好衛生規範(GHP),112/09/20 (查核),https://law.moj.gov.tw/LawClass/LawAll.aspx?pcode=L0040003,https://www.fda.gov.tw/media/333/ghp_checklist.pdf,食品良好衛生規範準則,食品業者之從業人員、作業場所、設施衛生管理及其品保制度，均應符合食品之良好衛生規範準則。包括倉儲管制、運輸管制、客訴處理、回收及銷毀、追溯追蹤等作業程序書之建立與執行。違反者經命限期改正，屆期不改正者，處新臺幣六萬元以上二億元以下罰鍰。,品保部 (QC)
7,追溯追蹤系統 (非追不可),113/02/01 (公告),https://ftracebook.fda.gov.tw/,https://ftracebook.fda.gov.tw/manual.pdf,食品追溯追蹤系統管理辦法,經中央主管機關公告類別及規模之食品業者，應建立食品及其相關產品追溯追蹤系統。應以電子方式申報追溯追蹤資料至中央主管機關建立之資訊系統(非追不可)。申報內容包括原材料來源、產品流向、庫存等資訊。應於每月10日前申報上個月之資料。,採購貿易部 (PT)
8,勞保/就保/職災保險,113/01/01 (費率調整),https://www.bli.gov.tw/,https://www.bli.gov.tw/rate_table_113.pdf,勞工保險條例及災保法,雇主應為勞工辦理勞工保險、就業保險及勞工職業災害保險。投保薪資應按勞工之月薪資總額，依投保薪資分級表之規定，向保險人申報。113年度起，勞保普通事故保險費率調整為12% (含就保1%)。雇主並應按月提繳不低於勞工每月工資6%之勞工退休金。,行政人事部 (AP)
9,個資安全維護計畫,113/04/25 (新修),https://law.moj.gov.tw/LawClass/LawAll.aspx?pcode=I0050021,https://www.ndc.gov.tw/media/555/pdpa_security_plan_template.doc,個人資料保護法第27條,非公務機關保有個人資料檔案者，應採行適當之安全措施，防止個人資料被竊取、竄改、毀損、滅失或洩漏。中央目的事業主管機關得指定非公務機關訂定個人資料檔案安全維護計畫或業務終止後個人資料處理方法。近期各部會(如經濟部、衛福部)皆已發布特定產業之安維辦法，要求業者定期演練並留存紀錄。,資訊部 (IT) / 法務部 (LG)`;

const CARD_COLLECTION = "compliance_cards";
const CRAWLER_LOG_COLLECTION = "compliance_crawler_runs";

// Simple CSV Parser
function parseSeedCsv(csv: string) {
    const lines = csv.split('\n').filter(l => l.trim().length > 0);
    const data = [];
    
    // Start from 1 to skip header
    for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        
        // CSV: 義務項目,公告標題,公告日期,公告連結,附檔連結,主旨,摘要,主責單位
        if (cols.length < 8) continue;
        
        data.push({
            no: parseInt(cols[0]),
            title: cols[1],
            dateText: cols[2],
            sourceUrl: cols[3],
            attachmentUrl: cols[4],
            subject: cols[5],
            summary: cols.slice(6, cols.length - 1).join(','),
            ownerDeptText: cols[cols.length - 1]
        });
    }
    return data;
}

export async function seedInitialProjectCards() {
    console.log("[Compliance] Checking 9 seed obligation cards...");
    
    const seeds = parseSeedCsv(SEED_CSV_CONTENT);
    const batch = db.batch();
    const collectionRef = collection(db, CARD_COLLECTION);
    
    // Fetch existing seeds to ID them
    const q = query(collectionRef, where("seedKey", "==", "obligation_checklist_9"));
    const snap = await getDocs(q);
    
    // Map existing by obligationNo
    const existingMap = new Map<number, any>();
    snap.docs.forEach(d => {
        const data = d.data();
        if (data.obligationNo) existingMap.set(data.obligationNo, { id: d.id, ...data });
    });

    for (const seed of seeds) {
        const existing = existingMap.get(seed.no);
        const mappedAgencyName = resolveAgencyName(seed.sourceUrl);
        
        const cardData: Partial<ComplianceCard> = {
            seedKey: "obligation_checklist_9",
            source: "csv_seed",
            obligationNo: seed.no,
            type: "project",
            riskLevel: "HIGH",
            agencyName: mappedAgencyName,
            
            // Section A is always synced from CSV
            sectionA: {
                title: seed.title,
                date: null,
                dateText: seed.dateText,
                announcementDate: null,
                subject: seed.subject,
                summary: seed.summary,
                sourceUrl: seed.sourceUrl,
                announcementUrl: seed.sourceUrl,
                attachmentLinks: seed.attachmentUrl ? [seed.attachmentUrl] : [],
                legalBasis: [],
                attachments: [] 
            },
        };
        
        const mappedCode = mapOwnerDeptToCode(seed.ownerDeptText);
        
        if (existing) {
            // Update mode: Only update Section A and Suggestion Text
            // NEVER overwrite Section B content, C content, status, flow
            const ref = doc(db, CARD_COLLECTION, existing.id);
            const mergedA = { ...existing.sectionA, ...cardData.sectionA };
            
            // Only set ownerDept if currently empty/unassigned to avoid overriding user selection
            let ownerDeptUpdate = existing.sectionB?.ownerDept;
            if (!ownerDeptUpdate && mappedCode) {
                 ownerDeptUpdate = getDeptNameByCode(mappedCode);
            }

            // Ensure coAssistSections structure exists
            let coAssistUpdate = existing.coAssistSections || [];
            // If migrating legacy sectionC to coAssist
            if (coAssistUpdate.length === 0 && existing.sectionC) {
                 coAssistUpdate.push({
                     id: "legacy_lg",
                     deptCode: "LG",
                     deptName: "法務部",
                     content: existing.sectionC.content || "",
                     attachments: existing.sectionC.attachments || []
                 });
            }
            // Ensure at least Legal exists if empty
            if (coAssistUpdate.length === 0) {
                 coAssistUpdate.push({
                     id: "default_lg",
                     deptCode: "LG",
                     deptName: "法務部",
                     content: "",
                     attachments: []
                 });
            }

            batch.update(ref, {
                sectionA: mergedA,
                agencyName: mappedAgencyName, // Update agency name
                "sectionB.ownerDeptSuggestionText": seed.ownerDeptText,
                "sectionB.ownerDept": ownerDeptUpdate || "",
                ownerDept: ownerDeptUpdate || "", // Sync top level
                coAssistSections: coAssistUpdate, // Migration/Init
                // Don't update updatedAt to avoid bumping sort if only seeding background check
            });
        } else {
            // Create New
            const newRef = collectionRef.doc();
            
            // Initial Co-Assist: Default Legal
            const initialCoAssist: CoAssistSection[] = [{
                id: "default_lg",
                deptCode: "LG",
                deptName: "法務部",
                content: "",
                attachments: []
            }];

            const fullCard: ComplianceCard = {
                id: newRef.id,
                ...cardData as any,
                status: "DRAFT",
                ownerDept: mappedCode ? getDeptNameByCode(mappedCode) : "",
                sectionB: {
                    ownerDept: mappedCode ? getDeptNameByCode(mappedCode) : "",
                    ownerDeptSuggestionText: seed.ownerDeptText,
                    content: "",
                    attachments: []
                },
                coAssistSections: initialCoAssist,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            batch.set(newRef, fullCard);
        }
    }
    
    await batch.commit();
}

/**
 * Force Refresh Section A from CSV for all seed cards.
 * Does NOT touch Section B, C (CoAssist), Status, or Flow.
 */
export async function refreshSeedCards() {
    console.log("[Compliance] Force refreshing seed cards from CSV...");
    const seeds = parseSeedCsv(SEED_CSV_CONTENT);
    const batch = db.batch();
    
    // Fetch all seed cards
    const q = query(collection(db, CARD_COLLECTION), where("seedKey", "==", "obligation_checklist_9"));
    const snap = await getDocs(q);
    
    // Map by obligationNo
    const existingMap = new Map<number, any>();
    snap.docs.forEach(d => {
        const data = d.data();
        if (data.obligationNo) existingMap.set(data.obligationNo, { id: d.id, ...data });
    });

    let updatedCount = 0;

    for (const seed of seeds) {
        const existing = existingMap.get(seed.no);
        if (!existing) continue; // Should not happen if seeded

        const ref = doc(db, CARD_COLLECTION, existing.id);
        const agencyName = resolveAgencyName(seed.sourceUrl);
        
        // Construct new Section A
        const newSectionA = {
            ...existing.sectionA, // Keep existing attachments/basis if any
            title: seed.title,
            dateText: seed.dateText,
            subject: seed.subject,
            summary: seed.summary,
            sourceUrl: seed.sourceUrl,
            announcementUrl: seed.sourceUrl,
            attachmentLinks: seed.attachmentUrl ? [seed.attachmentUrl] : []
        };

        batch.update(ref, {
            sectionA: newSectionA,
            agencyName: agencyName, // Update agency name
            "sectionB.ownerDeptSuggestionText": seed.ownerDeptText,
            // DO NOT update updatedAt to prevent re-sorting or appearing as "User Edit"
        });
        updatedCount++;
    }

    if (updatedCount > 0) {
        await batch.commit();
    }
    return updatedCount;
}

// CRUD (Existing)
export async function listComplianceCards(type?: ComplianceCardType, includeArchived = false): Promise<ComplianceCard[]> {
    let q = query(collection(db, CARD_COLLECTION), orderBy("updatedAt", "desc"));
    const snap = await getDocs(q);
    let cards = snap.docs.map(d => ({ id: d.id, ...d.data() } as ComplianceCard));

    if (type) {
        cards = cards.filter(c => c.type === type);
    }
    
    if (!includeArchived) {
        cards = cards.filter(c => !['ACK_ARCHIVED', 'COMPLETED', 'MOVED_TO_PROJECT'].includes(c.status));
    } else {
        cards = cards.filter(c => ['ACK_ARCHIVED', 'COMPLETED', 'MOVED_TO_PROJECT', 'APPROVED', 'REJECTED', 'FULLY_APPROVED'].includes(c.status));
    }
    
    // Migration Fix on read: Ensure coAssistSections exists
    cards = cards.map(c => {
        if (!c.coAssistSections && c.sectionC) {
            c.coAssistSections = [{
                id: "migrated_lg",
                deptCode: "LG",
                deptName: "法務部",
                content: c.sectionC.content || "",
                attachments: c.sectionC.attachments || []
            }];
        } else if (!c.coAssistSections) {
             c.coAssistSections = [];
        }
        return c;
    });

    return cards;
}

export async function createComplianceCard(data: Partial<ComplianceCard>): Promise<string> {
    const docRef = await addDoc(collection(db, CARD_COLLECTION), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
    return docRef.id;
}

export async function updateComplianceCard(id: string, data: Partial<ComplianceCard>): Promise<void> {
    await updateDoc(doc(db, CARD_COLLECTION, id), {
        ...data,
        updatedAt: serverTimestamp()
    });
}

export async function deleteComplianceCard(id: string, user: any) {
    const docRef = doc(db, CARD_COLLECTION, id);
    const snap = await getDoc(docRef);
    
    // FIX: Compat SDK uses property access for exists, Modular uses method
    const exists = typeof snap.exists === 'function' ? snap.exists() : snap.exists;
    if (!exists) return;
    
    const card = snap.data() as ComplianceCard;
    
    // Prevent delete if In Review (Double check)
    // IMPORTANT: Allow DELETE if user is super user OR card status is DRAFT
    // But block if IN_REVIEW
    if (['IN_REVIEW', 'APPROVED', 'PENDING_LEGAL', 'PENDING_MANAGER'].includes(card.status)) {
        throw new Error("審核中的專案不可刪除");
    }

    await deleteDoc(docRef);

    await logActivity({
        module: "govProject",
        action: "delete",
        entityId: id,
        entityType: "ComplianceCard",
        entityTitle: card.sectionA.title,
        actorId: user.id,
        actorName: user.displayName,
        details: "刪除專案卡片"
    });
}

export async function archiveCard(id: string, user: any) {
    await updateComplianceCard(id, {
        status: "ACK_ARCHIVED",
        archivedAt: serverTimestamp()
    });
    await logActivity({
        module: "govProject",
        action: "archive",
        entityId: id,
        entityType: "ComplianceCard",
        entityTitle: "法遵卡片歸檔",
        actorId: user.id,
        actorName: user.displayName,
        details: "知悉並歸檔"
    });
}

export async function moveIntelToProject(intelCard: ComplianceCard, user: any) {
    const newCard: Partial<ComplianceCard> = {
        type: "project",
        status: "DRAFT",
        riskLevel: intelCard.riskLevel,
        riskScore: intelCard.riskScore,
        sectionA: intelCard.sectionA, 
        sectionB: { ...intelCard.sectionB, content: "" }, 
        // Migrate legacy C to CoAssist
        coAssistSections: [{
            id: "default_lg", deptCode: "LG", deptName: "法務部", content: "", attachments: []
        }],
        agencyId: intelCard.agencyId,
        agencyName: intelCard.agencyName,
        ownerDept: intelCard.sectionB.ownerDept
    };
    
    await createComplianceCard(newCard);
    await updateComplianceCard(intelCard.id, { status: "MOVED_TO_PROJECT" });

    await logActivity({
        module: "govProject",
        action: "move_to_project",
        entityId: intelCard.id,
        entityType: "ComplianceCard",
        entityTitle: intelCard.sectionA.title,
        actorId: user.id,
        actorName: user.displayName
    });
}

export async function submitCardForApproval(card: ComplianceCard, user: any) {
    // 1. Check for existing active flow, and archive it to history if resubmitting
    if (card.approvalFlowId) {
        const oldFlowRef = doc(db, "approvalFlows", card.approvalFlowId);
        const oldFlowSnap = await getDoc(oldFlowRef);
        
        const exists = typeof oldFlowSnap.exists === 'function' ? oldFlowSnap.exists() : oldFlowSnap.exists;
        
        if (exists) {
            const oldFlowData = oldFlowSnap.data();
            const historyEntry = {
                flowId: card.approvalFlowId,
                archivedAt: new Date().toISOString(),
                finalStatus: oldFlowData?.status,
                data: oldFlowData
            };
            
            // Push to history array
            const newHistory = [...(card.approvalHistory || []), historyEntry];
            await updateComplianceCard(card.id, { approvalHistory: newHistory });
        }
    }

    // Prepare description from Co-Assist
    let legalContent = "";
    card.coAssistSections?.forEach(sec => {
        legalContent += `【${sec.deptName}協辦】\n${sec.content || "(無意見)"}\n\n`;
    });

    // 2. Create Approval Request (Standardized)
    const reqId = await createApprovalRequest({
        module: 'GOV_PROJECT',
        title: `法遵專案簽核：${card.sectionA.title}`,
        department: card.sectionB.ownerDept || "未指定部門",
        requesterUid: user.id,
        requesterName: user.displayName,
        priority: 'normal',
        sensitivity: 'normal',
        description: `【專案主旨】${card.sectionA.subject}\n【主責擬辦】\n${card.sectionB.content}\n\n${legalContent}`,
        status: 'IN_REVIEW',
        docCode: card.id,
        // Combine Attachments
        attachments: [
            ...(card.sectionA.attachments || []),
            ...(card.sectionB.attachments || []),
            ...(card.coAssistSections?.flatMap(c => c.attachments) || [])
        ]
    });

    // 3. Start Flow (Always creates/overwrites the active flow doc)
    const flow = await createOrResetApprovalFlow("GOV_PROJECT", card.id, user.id, card.sectionB.ownerDept, user.displayName);

    // 4. Update Card
    await updateComplianceCard(card.id, {
        status: "IN_REVIEW",
        approvalFlowId: flow.id
    });
}

// --- Crawler Utils ---

export async function getLatestCrawlerRun(): Promise<CrawlerRunLog | null> {
    const q = query(collection(db, CRAWLER_LOG_COLLECTION), orderBy("startedAt", "desc"), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const data = snap.docs[0].data();
    return { id: snap.docs[0].id, ...data } as CrawlerRunLog;
}

export async function runComplianceCrawlerNow(): Promise<CrawlerRunLog> {
    const runFn = functions.httpsCallable('runComplianceCrawlerNow');
    const res = await runFn();
    return res.data as CrawlerRunLog;
}
